import { useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { getMe, logoutApi } from "../api";
import type { AuthUser } from "../types/auth";
import type { RoleName } from "../constants/roles";
import { isAdmin } from "../constants/roles";
import { AuthContext } from "../hooks/useAuth";

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [viewAsRole, setViewAsRoleState] = useState<RoleName | null>(null);
  const [loading, setLoading] = useState(() => !!localStorage.getItem("access_token"));

  useEffect(() => {
    if (!localStorage.getItem("access_token")) return;
    getMe()
      .then((res) => setUser(res.data))
      .catch(() => {
        localStorage.removeItem("access_token");
        localStorage.removeItem("refresh_token");
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (viewAsRole && !isAdmin(user)) setViewAsRoleState(null);
  }, [user, viewAsRole]);

  const setViewAsRole = (role: RoleName | null) => {
    if (role && !isAdmin(user)) return;
    setViewAsRoleState(role);
  };

  const login = (accessToken: string, refreshToken?: string) => {
    localStorage.setItem("access_token", accessToken);
    if (refreshToken) localStorage.setItem("refresh_token", refreshToken);
    return getMe().then((res) => setUser(res.data));
  };

  const logout = async () => {
    const rt = localStorage.getItem("refresh_token");
    if (rt) {
      try { await logoutApi(rt); } catch { /* best-effort */ }
    }
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    setViewAsRoleState(null);
    setUser(null);
  };

  const refreshUser = () => getMe().then((res) => setUser(res.data));

  const effectiveUser = useMemo<AuthUser | null>(() => {
    if (!user) return null;
    if (!viewAsRole || !isAdmin(user)) return user;
    return { ...user, role: { ...(user.role ?? {}), name: viewAsRole } };
  }, [user, viewAsRole]);

  return (
    <AuthContext.Provider
      value={{
        user: effectiveUser,
        realUser: user,
        viewAsRole,
        setViewAsRole,
        loading,
        login,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
